import { ChangeEvent, useEffect, useState } from 'react';
import { Input, Select, Spinner } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { DateTimePicker } from 'components/DatePicker/DateTimePicker';
import { EarlyRepaymentSchedule } from 'components/EarlyRepaymentSchedule/EarlyRepaymentSchedule';
import {
  MIN_DE_CREDIT,
  MIN_UK_CREDIT,
} from 'components/StandAloneCalculator/filterConfigs.helper';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  endOfYesterday,
  isAfter,
  isBefore,
} from 'date-fns';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import {
  useGetDrawdownRepaymentSchedule,
  useGetIntroducerApplication,
} from 'lendingApi/lendingApiHooks';
import { dateToString } from 'utils/common/common';
import { getDurationInMonths } from 'utils/durationHelper';
import { TDrawdownRepaymentQueryParams } from 'utils/tests/urls.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';

import { CalculatorError } from './CalculatorError/CalculatorError';
import { getUpdatedMinimumRepayment } from './EarlyRepaymentCalculator.helpers';
import styles from './EarlyRepaymentCalculator.module.css';

type TProps = {
  formalOffer: TIntroducerApplication['formal_offer_sets'][number]['formal_offers'][number];
  selectedOfferMonthlyPayments: number;
};

export const EarlyRepaymentCalculator = ({
  formalOffer,
  selectedOfferMonthlyPayments,
}: TProps) => {
  const { introducerId } = useIntroducerData();
  const { application } = useGetIntroducerApplication();
  const defaultDate = dateToString(addMonths(new Date(), 1));
  const { formatCurrency } = useNumberFormat();
  const { trackEvent } = useAmplitude();
  const [inputAmount, setInputAmount] = useState(formalOffer.max_amount);
  const [amount, setAmount] = useState(formalOffer.max_amount);
  const [date, setDate] = useState<string | undefined>(defaultDate);
  const [selectedOption, setSelectedOption] = useState('1-month');
  const [overpaymentAmount, setOverpaymentAmount] = useState<number>();
  const [minimumMonthlyPayment, setMinimumMonthlyPayment] = useState<
    number | null
  >(null);
  const [isRecalculatingMinimum, setIsRecalculatingMinimum] = useState(false);
  const [inputError, setInputError] = useState('');
  const { isDE } = useLocale();
  const { data, isFetching, isError, error } = useGetDrawdownRepaymentSchedule({
    amount,
    formalOffer,
    date,
    overpaymentAmount,
    minimumMonthlyRepayment: minimumMonthlyPayment,
    isRecalculatingMinimum,
  });

  useEffect(() => {
    setMinimumMonthlyPayment(selectedOfferMonthlyPayments);
    setInputAmount(formalOffer.max_amount);
    setAmount(formalOffer.max_amount);
  }, [formalOffer.max_amount, selectedOfferMonthlyPayments]);

  const drawdownPdfQuery: TDrawdownRepaymentQueryParams = {
    amount,
    early_final_repayment_date: date,
    overpayment_repayment_amount: overpaymentAmount,
    minimum_monthly_repayment:
      selectedOfferMonthlyPayments &&
      Number(selectedOfferMonthlyPayments.toFixed(2)),
  };

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numberString = value.replace(/[^0-9]+/g, '');
    const amountNumber = parseInt(numberString, 10);

    setInputAmount(Number.isNaN(amountNumber) ? 0 : amountNumber);
  };
  const minAmount = isDE ? MIN_DE_CREDIT : MIN_UK_CREDIT;

  const handleAmountBlur = async (value: string) => {
    const inputNum = parseInt(value.replace(/[^0-9]/g, ''), 10) || 0;

    if (amount === inputNum) {
      return null;
    }

    if (inputNum < minAmount) {
      setInputError(t`Please enter a minimum of ${formatCurrency(minAmount)}`);
      setAmount(inputNum);
      setInputAmount(inputNum);
      return;
    }

    const cappedAmount = Math.min(inputNum, formalOffer.max_amount);
    setAmount(cappedAmount);
    setInputAmount(cappedAmount);

    if (introducerId && application?.application_uid) {
      setIsRecalculatingMinimum(true);
      const updatedMinimum = await getUpdatedMinimumRepayment(
        isDE,
        introducerId,
        cappedAmount,
        formalOffer,
        application.application_uid,
      );

      const newMinimum = updatedMinimum ?? minimumMonthlyPayment;
      setMinimumMonthlyPayment(newMinimum);
      setIsRecalculatingMinimum(false);
    }
  };

  const handleOverpaymentChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numberString = value.replace(/[^0-9]+/g, '');
    const amountNumber = parseInt(numberString, 10);

    if (!amountNumber && amountNumber !== 0) {
      setOverpaymentAmount(0);
      return;
    }

    if (amountNumber > formalOffer.max_amount) {
      setOverpaymentAmount(formalOffer.max_amount);
      return;
    }

    setOverpaymentAmount(amountNumber);
  };

  const setDateWithTracking = (value: string, newDate?: Date) => {
    trackEvent(AMPLITUDE_EVENTS.earlyRepaymentCalculatorDateChanged, {
      Label: value,
      'Repayment duration (days)': newDate
        ? differenceInDays(newDate, new Date())
        : 'Full length',
    });

    if (!newDate) {
      // If date is undefined, API returns full length of repayment schedule
      setDate(undefined);
      return;
    }
    setDate(dateToString(newDate));
  };

  const handleRepaymentChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    switch (value) {
      case '1-month':
      case '3-months':
      case '6-months':
      case '9-months': {
        setSelectedOption(value);
        setOverpaymentAmount(undefined);
        // Get only the month from the value
        const newDate = addMonths(new Date(), parseInt(value.split('-')[0]));
        return setDateWithTracking(value, newDate);
      }
      case 'full-term': {
        setSelectedOption('full-term');
        setOverpaymentAmount(undefined);
        return setDateWithTracking(value, undefined);
      }
      case 'custom': {
        setSelectedOption('custom');
        setOverpaymentAmount(undefined);
        break;
      }
      case 'monthly-amount': {
        setDate(undefined);
        setSelectedOption('monthly-amount');
        break;
      }
    }
  };

  const getLoanEndDate = () => {
    switch (formalOffer.duration.unit) {
      case 'days':
        return addDays(new Date(), formalOffer.duration.amount);
      case 'months':
        return addMonths(new Date(), formalOffer.duration.amount);
      case 'years':
        return addYears(new Date(), formalOffer.duration.amount);
    }
  };

  const filterCustomDates = (date: Date) => {
    return isBefore(date, getLoanEndDate()) && isAfter(date, endOfYesterday());
  };

  const renderFullTermLabel = () => {
    const duration = getDurationInMonths(formalOffer.duration);
    if (!duration) {
      return null;
    }
    return t`in ${duration} months`;
  };

  const renderValue = (label: string, value?: number) => {
    if ((!isFetching && !data) || isError) {
      return <p className={styles.summaryValue}>-</p>;
    }

    return (
      <>
        {isFetching || !data ? (
          <Spinner
            dataTestId={`${label}-valueSpinner`}
            className={styles.valueSpinner}
          />
        ) : (
          <p className={styles.summaryValue}>{formatCurrency(value)}</p>
        )}
      </>
    );
  };

  useEffect(() => {
    if (selectedOption !== 'monthly-amount' || overpaymentAmount === null) {
      return;
    }

    if (
      minimumMonthlyPayment &&
      overpaymentAmount !== undefined &&
      overpaymentAmount < minimumMonthlyPayment
    ) {
      setInputError(
        t`Repayment amount should be higher than minimum monthly repayment`,
      );
    } else {
      setInputError('');
    }
  }, [overpaymentAmount, minimumMonthlyPayment, selectedOption]);

  useEffect(() => {
    if (selectedOption !== 'monthly-amount' && inputAmount > minAmount) {
      setInputError('');
    }
  }, [inputAmount, minAmount, selectedOption, selectedOfferMonthlyPayments]);

  return (
    <div
      data-testid="early-repayment-calculator"
      className={styles.earlyRepaymentCalculator}
    >
      <h2 className={styles.heading}>
        <Trans>Calculate repayment</Trans>
      </h2>
      <div className={styles.repaymentOptions}>
        <div className={styles.repaymentOption}>
          <Trans>If the applicant borrows</Trans>
          <Input
            data-testid="amount-input"
            className={styles.repaymentOptionInput}
            onChange={handleAmountChange}
            onBlur={(e) => handleAmountBlur(e.target.value)}
            value={formatCurrency(inputAmount)}
          />
        </div>
        <div className={styles.repaymentOption}>
          <Trans>and repays</Trans>
          <div className={styles.dateInputs}>
            <Select
              data-testid="option-select"
              className={styles.repaymentOptionInput}
              onChange={handleRepaymentChange}
              defaultValue={selectedOption}
            >
              <option disabled value="">
                <Trans>Select an option</Trans>
              </option>
              <option value="1-month">
                <Trans>in 1 month</Trans>
              </option>
              <option value="6-months">
                <Trans>in 6 months</Trans>
              </option>
              <option value="9-months">
                <Trans>in 9 months</Trans>
              </option>
              <option value="full-term">{renderFullTermLabel()}</option>
              <option value="custom">
                <Trans>by custom date</Trans>
              </option>
              <option value="monthly-amount">
                <Trans>by monthly amount</Trans>
              </option>
            </Select>
            {selectedOption === 'custom' && (
              <div className={styles.customDate}>
                <DateTimePicker
                  filterDate={filterCustomDates}
                  handleChange={(date: Date | null) =>
                    date && setDate(dateToString(date))
                  }
                  withInput
                  defaultDate={defaultDate}
                />
              </div>
            )}
            {selectedOption === 'monthly-amount' && (
              <Input
                data-testid="montly-amount-input"
                className={styles.monthlyPaymentInput}
                onChange={handleOverpaymentChange}
                value={formatCurrency(overpaymentAmount)}
              />
            )}
          </div>
          {isDE && <div>zurückzahlt</div>}
        </div>
      </div>
      {(inputError || error) && <CalculatorError error={inputError || error} />}
      <div className={styles.repaymentValues}>
        <div className={styles.summaryItem}>
          <p className={styles.summaryHeading}>
            <Trans>Early repayment fee</Trans>:
          </p>
          {renderValue('repaymentFee', 0)}
        </div>
        <div className={styles.summaryItem}>
          <p className={styles.summaryHeading}>
            <Trans>Total fees + interest</Trans>:
          </p>
          {renderValue(
            'fees',
            (data?.data.summary.total_fees || 0) +
              (data?.data.summary.total_interest || 0),
          )}
        </div>
        <div className={styles.summaryItem}>
          <p className={styles.summaryHeading}>
            <Trans>Total repayment</Trans>:
          </p>
          {renderValue('totalRepayment', data?.data.summary.total_payable)}
        </div>
        <div className={clsx(styles.moneySaved, styles.summaryItem)}>
          <p className={styles.summaryHeading}>
            <Trans>Total savings</Trans>:
          </p>
          {renderValue('totalSavings', data?.data.summary.amount_saved || 0)}
        </div>
      </div>
      {data && (
        <EarlyRepaymentSchedule
          schedule={data?.data.schedule}
          summary={data?.data.summary}
          formalOffer={formalOffer}
          isCustomDate={selectedOption === 'custom'}
          pdfQuery={drawdownPdfQuery}
        />
      )}
    </div>
  );
};
